import { Grid, Typography } from '@mui/material';
import { getBillingIntervalInMonths, getBillingIntervalsInYear,
  isCommissionProduct, isFixedPerUnitProduct,
  isInstallmentsProduct,
  isRefundProduct,
  isSubscriptionProduct } from '@weft/services';
import type { BillingInterval, Currency, FixedProductInput } from '@weft/types';
import { withDefault } from '@weft/utils';
import { formatNumber } from '@weft/utils';
import React from 'react';
import { Price } from '../../../../../../components/design/Price';
import { getFixedProductEstimation } from '../utils/live-preview-estimation';

type FixedProductItemProps = {
  product: FixedProductInput;
  billingInterval: BillingInterval;
  currency: Currency;
};

export const FixedProductItem: React.FC<FixedProductItemProps> = ({ product, billingInterval, currency }) => {
  return (
    <Grid
      component="li"
      container
    >
      <Grid
        item
        xs={6}
      >
        <Typography
          color="#111B1E"
          fontWeight={400}
          fontSize="12px"
          lineHeight="16px"
        >
          {withDefault(product.variants?.[0].name, '')}
        </Typography>
      </Grid>
      <Grid
        item
        xs={6}
        textAlign="right"
      >
        <Typography
          color="#9DA8AC"
          fontWeight={300}
          fontSize="12px"
          lineHeight="16px"
        >
          {getPriceLabel(product, billingInterval, currency)}
        </Typography>
      </Grid>
      <Grid
        item
        xs={6}
      >
        <Typography
          color="#94A0A4"
          fontWeight={300}
          fontSize="10px"
          lineHeight="12px"
        >
          {withDefault(product.variants?.[0].description, 'No Description')}
        </Typography>
      </Grid>
      <Grid
        item
        xs={6}
        textAlign="right"
      >
        <Typography
          color="#111B1E"
          fontWeight={400}
          fontSize="12px"
          lineHeight="16px"
        >
          {getPrice(product, billingInterval, currency)}
        </Typography>
      </Grid>
    </Grid>
  );
};

function getPriceLabel(product: FixedProductInput, billingInterval: BillingInterval, currency: Currency): React.ReactNode {
  if (isSubscriptionProduct(product)) {
    const billingIntervalInMonths = getBillingIntervalInMonths(billingInterval);
    const months = getBillingIntervalsInYear(billingInterval);
    const price = withDefault(product.variants[0].pricing.priceConfiguration?.price * billingIntervalInMonths, 0);

    return (
      <span className="flex justify-end gap-x-1">
        <span>{months}</span>
        <span>X</span>
        <Price
          align="unset"
          currency={currency}
          color="#9DA8AC"
          fontWeight={300}
          fontSize="12px"
          lineHeight="16px"
        >
          {price}
        </Price>
      </span>
    );
  }

  if (isRefundProduct(product)) {
    return (
      <span className="flex justify-end gap-x-1">
        <span>Refund</span>
      </span>
    );
  }
  
  if (isInstallmentsProduct(product)) {
    const installments = withDefault(product.variants[0].pricing.priceConfiguration?.installments, 0);
    const price = withDefault(product.variants[0].pricing.priceConfiguration?.price, 0);

    return (
      <span className="flex justify-end gap-x-1">
        <span>{installments}</span>
        <span>X</span>
        <Price
          align="unset"
          currency={currency}
          color="#9DA8AC"
          fontWeight={300}
          fontSize="12px"
          lineHeight="16px"
        >
          {price}
        </Price>
      </span>
    );
  }

  if (isFixedPerUnitProduct(product)) {
    const units = withDefault(product.variants[0].pricing.priceConfiguration?.units, 0);
    const price = withDefault(product.variants[0].pricing.priceConfiguration?.price, 0);

    return (
      <span className="flex justify-end gap-x-1">
        <span>{units}</span>
        <span>X</span>
        <Price
          align="unset"
          currency={currency}
          color="#9DA8AC"
          fontWeight={300}
          fontSize="12px"
          lineHeight="16px"
        >
          {price}
        </Price>
      </span>
    );
  }

  return 'Amount';
}

function getPrice(product: FixedProductInput, billingInterval: BillingInterval, currency: Currency): React.ReactNode {
  if (isCommissionProduct(product)) {
    const percentage = withDefault(product.variants[0].pricing.priceConfiguration?.percentage, 0);

    return `%${formatNumber(percentage)}`;
  }

  if (isSubscriptionProduct(product)) {
    const billingIntervalInMonths = getBillingIntervalInMonths(billingInterval);
    const price = withDefault(product.variants[0].pricing.priceConfiguration?.price * billingIntervalInMonths, 0);

    return (
      <Price
        currency={currency}
        align="right"
        color="#111B1E"
        fontWeight={400}
        fontSize="12px"
        lineHeight="16px"
      >
        {price}
      </Price>
    );
  }

  return (
    <Price
      currency={currency}
      align="right"
      color="#111B1E"
      fontWeight={400}
      fontSize="12px"
      lineHeight="16px"
    >
      {getFixedProductEstimation(product, billingInterval)}
    </Price>
  );
}
